import type { ApplicationRequest } from '../types/applications'
import { getTagsApprovalObject, TagsRequest } from '../types/tags'
import type {
	ApplicationInfrastructureType,
	ApplicationTargetResolution
} from './applicationTargetResolver'
import { normalizeApplicationInfrastructureType } from './applicationTargetResolver'

export type BuildApplicationPayloadInput = {
	requestorID: string
	formValues: Record<string, any>
	tagFormValues: Record<string, any>
	inheritedTagsActive: boolean
	generatedTagsID: string
	creationTime: string
}

type BuiltApplicationRequest = ApplicationRequest & {
	tagsID: string
	wkldClusterName: string
}

type BuiltApplicationPayloads = {
	tagsRequest?: TagsRequest
	application: BuiltApplicationRequest
}

type SubmitCallbacks = {
	onSuccess: () => void
	onError: (error: any) => void
}

type SubmitApplicationPayloadsInput = BuiltApplicationPayloads & {
	createTags: (tagsRequest: TagsRequest, callbacks: SubmitCallbacks) => unknown
	createNewApplication: (
		application: BuiltApplicationRequest,
		callbacks: SubmitCallbacks
	) => unknown
	callbacks: SubmitCallbacks
}

type ApplicationSubmitPayloadPreparation =
	| {
			status: 'ready'
			payloads: BuiltApplicationPayloads
	  }
	| {
			status: 'failed'
			errorMessage: string
	  }

const isRecord = (value: unknown): value is Record<string, any> =>
	Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const getOptionString = (value: unknown): string => {
	if (isRecord(value)) {
		return String(value.value ?? value.label ?? '').trim()
	}

	return String(value ?? '').trim()
}

const getDisplayString = (value: unknown): string => {
	if (isRecord(value)) {
		return String(value.label ?? value.value ?? '').trim()
	}

	return String(value ?? '').trim()
}

const getNormalizedBlankableString = (value: unknown) => {
	const normalized = getOptionString(value)
	return normalized || 'na'
}

const getSelectionValue = (value: unknown) => getOptionString(value)

const getSelectedTarget = (
	formValues: Record<string, any>
): ApplicationTargetResolution | undefined =>
	formValues.gcpProjectID?.resolution as ApplicationTargetResolution | undefined

const getSelectedInfrastructureType = (
	formValues: Record<string, any>,
	target: ApplicationTargetResolution
): ApplicationInfrastructureType | undefined =>
	normalizeApplicationInfrastructureType(formValues.infrastructureType) ||
	(target.supportsGke ? undefined : 'non-gke')

const getPortfolio = (value: unknown) => {
	if (isRecord(value)) {
		const label = String(value.label ?? value.value ?? '').trim()
		const optionValue = String(value.value ?? value.label ?? '').trim()

		return { label, value: optionValue }
	}

	const optionValue = getOptionString(value)
	return { label: optionValue, value: optionValue }
}

const getApplicationViewers = (value: unknown, fallback: string[] = []) => {
	if (Array.isArray(value)) {
		return value.map((viewer) => String(viewer ?? '').trim()).filter(Boolean)
	}

	if (typeof value === 'string') {
		return value
			.split(',')
			.map((viewer) => viewer.trim())
			.filter(Boolean)
	}

	return fallback
}

const getTagDetails = (target: ApplicationTargetResolution) =>
	target.inheritedTagDetails || target.selectedProject?.tags?.details || {}

const getSelectedProjectID = (
	formValues: Record<string, any>,
	target: ApplicationTargetResolution
) =>
	getDisplayString(target.selectedProject?.gcpProjectID) ||
	getSelectionValue(formValues.gcpProjectID)

const getSelectedProjectName = (
	formValues: Record<string, any>,
	target: ApplicationTargetResolution
) => {
	const selectedProjectID = getSelectedProjectID(formValues, target)

	return (
		getDisplayString(target.selectedProject?.gcpProjectName) ||
		getDisplayString(target.selectedProject?.details?.gcpProjectName) ||
		getDisplayString(target.selectedProject?.details?.projectName) ||
		getDisplayString(formValues.gcpProjectID) ||
		selectedProjectID
	)
}

const getPlatformOwner = (
	tagFormValues: Record<string, any>,
	target: ApplicationTargetResolution
) => {
	const tagDetails = getTagDetails(target)

	return (
		getDisplayString(tagFormValues.platformOwner) ||
		getDisplayString(tagDetails.platformOwner) ||
		getDisplayString(target.selectedProject?.tags?.details?.platformOwner) ||
		getDisplayString(target.selectedProject?.details?.platformOwner) ||
		'N/A'
	)
}

const getPlatformOwnerEmail = (
	formValues: Record<string, any>,
	target: ApplicationTargetResolution
) => {
	const tagDetails = getTagDetails(target)

	return (
		getDisplayString(tagDetails.platformOwnerEmail) ||
		getDisplayString(
			target.selectedProject?.tags?.details?.platformOwnerEmail
		) ||
		getDisplayString(target.selectedProject?.details?.platformOwnerEmail) ||
		getDisplayString(formValues.platformOwnerEmail) ||
		'N/A'
	)
}

const getBaseApplicationDetails = (
	input: BuildApplicationPayloadInput,
	target: ApplicationTargetResolution
) => ({
	applicationName: getDisplayString(input.formValues.applicationName),
	applicationAlias: getDisplayString(input.formValues.applicationAlias),
	applicationViewer: getApplicationViewers(
		input.formValues.applicationViewer,
		target.applicationViewer
	),
	applicationOwner: getDisplayString(input.formValues.applicationOwner),
	applicationOwnerEmail: getDisplayString(input.formValues.applicationOwnerEmail),
	sourceEnvName: getDisplayString(input.formValues.sourceEnvName),
	additionalEnvName: getDisplayString(input.formValues.additionalEnvName),
	sitEnvironmentName: input.formValues.sitEnvironmentName,
	wbsCode: getNormalizedBlankableString(input.formValues.wbsCode),
	costCenter: getNormalizedBlankableString(input.formValues.costCenter),
	deployment:
		getOptionString(input.formValues.deployment) ||
		getDisplayString(target.deployment),
	dataClassification: getOptionString(input.formValues.dataClassification),
	tierLevel: getOptionString(input.formValues.tierLevel),
	group: getPortfolio(target.portfolio),
	platformOwner: getPlatformOwner(input.tagFormValues, target),
	platformOwnerEmail: getPlatformOwnerEmail(input.formValues, target)
})

const getGkeTargetDetails = (
	input: BuildApplicationPayloadInput,
	target: ApplicationTargetResolution
) => {
	const cluster = target.resolvedCluster || {}
	const projectName = getSelectedProjectName(input.formValues, target)
	const tagDetails = getTagDetails(target)

	return {
		platformID: getDisplayString(cluster.platformID),
		platformName: {
			label: getDisplayString(cluster.platformName),
			value: getDisplayString(cluster.platformID)
		},
		platformAlias:
			getDisplayString(tagDetails.systemAlias) ||
			getDisplayString(tagDetails.platformAlias) ||
			getDisplayString(input.formValues.platformAlias) ||
			'custom',
		gcpProjectID: projectName,
		projectId: projectName,
		environment:
			getDisplayString(cluster.environment) ||
			getOptionString(input.formValues.environment),
		wkldClusterName: getDisplayString(cluster.namePrefix) || 'N/A'
	}
}

const getNonGkeTargetDetails = (
	input: BuildApplicationPayloadInput,
	target: ApplicationTargetResolution
) => {
	const tagDetails = getTagDetails(target)
	const selectedProjectID = getSelectedProjectID(input.formValues, target)
	const projectName = getSelectedProjectName(input.formValues, target)
	const platformName =
		getDisplayString(tagDetails.platformName) ||
		projectName ||
		selectedProjectID
	const platformAlias =
		getDisplayString(tagDetails.systemAlias) ||
		getDisplayString(tagDetails.platformAlias) ||
		platformName.substring(0, 5).toLowerCase() ||
		'custom'

	return {
		platformID: selectedProjectID,
		platformName,
		platformAlias,
		gcpProjectID: projectName,
		projectId: projectName,
		environment: getOptionString(input.formValues.environment),
		wkldClusterName: 'N/A'
	}
}

// Only reachable if an application is ever submitted without inheriting its GCP Project's
// tags. The GCP onboarding flow always inherits, so no tags record is created there and the
// per-application approvals are appended to the project's record by the backend instead.
const buildTagsRequest = (
	input: BuildApplicationPayloadInput,
	target: ApplicationTargetResolution
): TagsRequest => ({
	requestorID: input.requestorID,
	tagsID: input.generatedTagsID,
	approvalStatus: getTagsApprovalObject(),
	details: {
		...input.tagFormValues,
		platformOwner: getPlatformOwner(input.tagFormValues, target),
		WBSCode: getNormalizedBlankableString(input.tagFormValues.WBSCode),
		costCenter: getNormalizedBlankableString(input.tagFormValues.costCenter)
	},
	action: 'CREATE',
	status: 'PENDING_APPROVAL'
})

export const getApplicationSubmitTargetFields = () => ['gcpProjectID']

export const prepareApplicationSubmitPayloads = (
	input: BuildApplicationPayloadInput
): ApplicationSubmitPayloadPreparation => {
	const target = getSelectedTarget(input.formValues)

	if (target?.status !== 'ready') {
		return {
			status: 'failed',
			errorMessage:
				target?.message ||
				'The selected GCP Project is not ready for application onboarding.'
		}
	}

	try {
		return {
			status: 'ready',
			payloads: buildApplicationPayloads(input)
		}
	} catch (error) {
		const errorMessage =
			error instanceof Error ? error.message : 'Unknown payload builder error'

		return {
			status: 'failed',
			errorMessage: `Application payload could not be prepared: ${errorMessage}`
		}
	}
}

export const buildApplicationPayloads = (
	input: BuildApplicationPayloadInput
): BuiltApplicationPayloads => {
	const target = getSelectedTarget(input.formValues)

	if (target?.status !== 'ready') {
		throw new Error('The selected GCP Project is not ready for application onboarding')
	}

	if (!target.portfolio?.value) {
		throw new Error('The selected GCP Project does not have a Portfolio')
	}

	const infrastructureType = getSelectedInfrastructureType(
		input.formValues,
		target
	)

	if (!infrastructureType) {
		throw new Error('Please select an infrastructure type for your application')
	}

	if (infrastructureType === 'gke' && !target.resolvedCluster) {
		throw new Error('GKE application target must resolve an approved cluster')
	}

	const targetDetails =
		infrastructureType === 'gke'
			? getGkeTargetDetails(input, target)
			: getNonGkeTargetDetails(input, target)
	const tagsID = input.inheritedTagsActive
		? target.inheritedTagsID || ''
		: input.generatedTagsID

	const application: BuiltApplicationRequest = {
		requestorID: input.requestorID,
		tagsID,
		action: 'CREATE',
		wkldClusterName: targetDetails.wkldClusterName,
		details: {
			...getBaseApplicationDetails(input, target),
			infrastructureType,
			platformID: targetDetails.platformID,
			platformName: targetDetails.platformName,
			platformAlias: targetDetails.platformAlias,
			gcpProjectID: targetDetails.gcpProjectID,
			projectId: targetDetails.projectId,
			environment: targetDetails.environment
		},
		status: 'PENDING_APPROVAL',
		creationTime: input.creationTime
	}

	if (input.inheritedTagsActive) {
		return { application }
	}

	return {
		tagsRequest: buildTagsRequest(input, target),
		application
	}
}

export const submitApplicationPayloads = ({
	tagsRequest,
	application,
	createTags,
	createNewApplication,
	callbacks
}: SubmitApplicationPayloadsInput) => {
	if (tagsRequest) {
		createTags(tagsRequest, {
			onSuccess: () => createNewApplication(application, callbacks),
			onError: callbacks.onError
		})
		return
	}

	createNewApplication(application, callbacks)
}
